// ENG-4807: pure decision function — should the manager restart an agent's
// persistent session because its channel set changed?
//
// Why this exists: when a channel is added or removed for a live persistent
// claude-code session, the parent claude was launched with stale flags
// (`--dangerously-load-development-channels` and `--allowedTools`). Those
// flags are baked at spawn time and don't pick up the new channel. The
// only fix is to kill the tmux session so the manager respawns it.
// See ENG-4807 for the full diagnosis.
//
// Pulled out as a pure function so the gating logic is unit-testable
// without mocking processAgent's dozens of dependencies.

export interface ChannelRestartDecisionInput {
  /**
   * The channel ids the manager saw on the *previous* refresh tick for
   * this agent, or `undefined` if this is the first time we've seen the
   * agent (first poll, or fresh manager process). On first poll the
   * persistent session is either not started yet or about to be started
   * with the fresh launch flags, so a restart would be a noisy no-op.
   */
  previousChannelIds: Set<string> | undefined;
  /** The channel ids in this refresh tick's response. */
  currentChannelIds: Set<string>;
  /**
   * Agent's `session_mode` from the refresh response. Restart only
   * applies to `persistent` sessions — oneshot agents don't have a
   * tmux session whose launch flags need refreshing.
   */
  sessionMode: string | undefined;
  /**
   * Agent's framework. Channel listener flags are claude-code-specific;
   * other frameworks (openclaw, nemoclaw) don't use the
   * `--dangerously-load-development-channels` mechanism so the restart
   * doesn't apply.
   */
  framework: string;
  /**
   * Whether the agent's tmux session is currently alive. If it isn't,
   * there's nothing to kill — the manager will spawn a fresh one
   * downstream with the right flags.
   */
  sessionHealthy: boolean;
}

export interface ChannelRestartDecision {
  /** True iff the manager should kill the tmux session. */
  restart: boolean;
  /** Channels that appeared in this tick (informational, for log line). */
  added: string[];
  /** Channels that disappeared in this tick (informational, for log line). */
  removed: string[];
}

/**
 * ENG-4807 (CodeRabbit on PR #773 round 4): the channel set we care about
 * for restart purposes is NOT every key in `channel_configs` — it's only
 * the channels that the running session would actually load as channel
 * listeners. Those are the ones the manager calls
 * `writeChannelCredentials` for, gated on `status ∈ {active, pending}`
 * AND a non-null `config`. A status-only flip (e.g. active → revoked)
 * removes the channel from the launchable set even though its row stays
 * in `channel_configs`, and therefore should trigger a restart so the
 * launch flags drop the now-unauthorised channel. Inverse: a row that
 * never becomes launchable (e.g. always errored) should NOT trigger a
 * restart.
 *
 * Pure helper so the launchable filter has the same unit-test coverage
 * as the rest of the decision logic.
 */
export interface ChannelConfigEntry {
  status?: string;
  config?: unknown;
}

export function launchableChannelIds(
  channelConfigs: Record<string, ChannelConfigEntry | undefined> | null | undefined,
): Set<string> {
  if (!channelConfigs) return new Set();
  const result = new Set<string>();
  for (const [channelId, entry] of Object.entries(channelConfigs)) {
    if (!entry) continue;
    if (entry.config == null) continue;
    if (entry.status !== 'active' && entry.status !== 'pending') continue;
    result.add(channelId);
  }
  return result;
}

export function decideChannelRestart(input: ChannelRestartDecisionInput): ChannelRestartDecision {
  const { previousChannelIds, currentChannelIds, sessionMode, framework, sessionHealthy } = input;

  // First poll for this agent — nothing to restart, the session (if any)
  // either doesn't exist yet or was just spawned with the right flags.
  if (previousChannelIds === undefined) {
    return { restart: false, added: [], removed: [] };
  }

  // Compute the diff regardless — it's used in the log line even when we
  // ultimately don't restart, and it's cheap.
  const added = [...currentChannelIds].filter((c) => !previousChannelIds.has(c));
  const removed = [...previousChannelIds].filter((c) => !currentChannelIds.has(c));

  // No actual set change — nothing to do (debounces same-set writes that
  // happen on every refresh tick once the cache is warm).
  if (added.length === 0 && removed.length === 0) {
    return { restart: false, added, removed };
  }

  // Restart only applies to live persistent claude-code sessions.
  if (sessionMode !== 'persistent') return { restart: false, added, removed };
  if (framework !== 'claude-code') return { restart: false, added, removed };
  if (!sessionHealthy) return { restart: false, added, removed };

  return { restart: true, added, removed };
}
